import * as Api from '@ViewModels';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { startOfDay } from 'date-fns';
import * as React from 'react';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { formatDateCriteria } from '../../../../models/UiUtils';
import { reactQueryClient } from '../../../ReactQueryProvider';
import { IDateRange } from '../utils/dateRange';
import { CALENDAR_ACTION_ITEMS_QUERY_KEY as BASE_QUERY_KEY } from './calendarActionItemsQueryKey';

/** Fetch a generous page so the whole visible window usually lands in one request. */
const CALENDAR_PAGE_SIZE = 100;

/**
 * When today is visible, also fetch action items due up to this many days before the window so
 * overdue items can be rolled onto today. Bounds the fetch so we don't pull the entire history.
 */
const OVERDUE_LOOKBACK_DAYS = 180;

/**
 * Safety cap on the eager page-drain. At {@link CALENDAR_PAGE_SIZE} per page this covers far
 * more due-dated action items than any single visible window realistically holds; it exists
 * only so a pathological window can't spin through unbounded requests.
 */
const MAX_AUTO_PAGES = 10;

export interface IUseCalendarActionItemsResult {
	actionItems: Api.IActionItem[];
	/** True until the first page resolves. */
	isLoading: boolean;
}

/**
 * Fetch action items whose due date falls inside the visible calendar window, translating the
 * window into a `DueDate` range criterion (matching the Action Items list's filter shape) and
 * paging through results so callers receive a single flat list. Scope is controlled by
 * `assignedToUserId`: a user id restricts to that assignee; `undefined` returns every user's
 * action items in the account ("everyone's"). Honors the admin impersonation context.
 */
export function useCalendarActionItems({
	range,
	assignedToUserId,
	search = '',
	includeCompleted = false,
	impersonationContext,
	enabled = true,
}: {
	range: IDateRange;
	/** Restrict to one assignee; omit/undefined to include all users' action items. */
	assignedToUserId?: string;
	/** Free-text search over action items (matches the list's search). */
	search?: string;
	/** Include completed action items (defaults to in-progress only). */
	includeCompleted?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	enabled?: boolean;
}): IUseCalendarActionItemsResult {
	const userSession = useUserSession();

	const filterRequest = React.useMemo<Api.IRichContentRequest>(() => {
		const criteria: Api.IRichContentRequest['criteria'] = [];
		// When today is in the visible window, pull back the lower bound so overdue items (due
		// before the window) come along to be rolled onto today; otherwise fetch just the window.
		const now = new Date();
		const todayInRange = range.start.getTime() <= now.getTime() && now.getTime() <= range.end.getTime();
		const fetchStart = todayInRange
			? new Date(
					Math.min(range.start.getTime(), startOfDay(now).getTime() - OVERDUE_LOOKBACK_DAYS * 24 * 60 * 60 * 1000)
				)
			: range.start;
		const dateCriteria = formatDateCriteria<Api.RichContentProperty>(Api.RichContentProperty.DueDate, {
			start: fetchStart,
			end: range.end,
		});
		if (dateCriteria) {
			criteria?.push(dateCriteria);
		}
		if (!includeCompleted) {
			criteria?.push({ property: Api.RichContentProperty.IsCompleted, value: 'false' });
		}
		criteria?.push({ property: Api.RichContentProperty.Type, value: 'ActionItem' });
		// A specific assignee id scopes to that user; `undefined` leaves it unrestricted (all users).
		criteria?.push({ property: Api.RichContentProperty.AssignedTo, value: assignedToUserId });
		// The filter endpoint does free-text search via a `Content` criterion, not a query param —
		// this mirrors how the Action Items list's VM applies its search.
		const trimmedSearch = search.trim();
		if (trimmedSearch) {
			criteria?.push({ property: Api.RichContentProperty.Content, value: trimmedSearch });
		}
		return { criteria };
	}, [range.start, range.end, assignedToUserId, includeCompleted, search]);

	const query = useInfiniteQuery<Api.IPagedCollection<Api.IActionItem>>({
		enabled,
		placeholderData: keepPreviousData,
		getNextPageParam: lastPage => lastPage.pageToken,
		queryFn: async ({ pageParam }) => {
			return userSession.webServiceHelper.callAsync<Api.IPagedCollection<Api.IActionItem>>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					queryParams: {
						currentUserOnly: false,
						pageSize: CALENDAR_PAGE_SIZE,
						pageToken: pageParam,
						sortBy: 'dueDate',
						sort: 'asc',
					},
					urlPath: BASE_QUERY_KEY,
				}),
				'POST',
				filterRequest
			);
		},
		queryKey: [
			BASE_QUERY_KEY,
			filterRequest,
			assignedToUserId,
			search,
			includeCompleted,
			impersonationContext?.account?.id,
		],
		initialPageParam: '',
		meta: { toastErrorMessage: 'Failed to load action items' },
	});

	const { hasNextPage, isFetchingNextPage, fetchNextPage, isFetchNextPageError } = query;
	const pagesLoaded = query.data?.pages.length ?? 0;

	// See `useCalendarMeetings` for why the error guard is required to bound this drain.
	React.useEffect(() => {
		if (enabled && hasNextPage && !isFetchingNextPage && !isFetchNextPageError && pagesLoaded < MAX_AUTO_PAGES) {
			fetchNextPage();
		}
	}, [enabled, hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage, pagesLoaded]);

	const actionItems = React.useMemo(() => query.data?.pages.flatMap(page => page.values) ?? [], [query.data?.pages]);

	return {
		actionItems,
		isLoading: enabled ? query.isPending : false,
	};
}

export const invalidateAllCalendarActionItems = () => {
	reactQueryClient.invalidateQueries({ queryKey: [BASE_QUERY_KEY] });
};
